if (__DEV__) {
  require("./ReactotronConfig");
}
import {
  SendbirdUIKitContainer
} from '@sendbird/uikit-react-native';
import { ErrorBoundary } from '@shared/components/ErrorBoundary';
import I18nProvider from '@shared/i18n/I18nProvider';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  LightUIKitTheme,
  Palette,
  createTypography,
} from '@sendbird/uikit-react-native-foundation';
import { AuthProvider } from 'app/auth/contexts/AuthContext';
import { UserProvider } from 'app/auth/contexts/UserContext';
import { PlatformServices } from 'app/services/SendbirdServices';
import { PushNotificationInitializer } from 'app/services/PushNotificationInitializer';
import { handleNotificationNavigation } from 'app/services/NavigationService';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { KeyboardProvider } from 'react-native-keyboard-controller';
import notifee, { EventType } from '@notifee/react-native';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusBar } from 'react-native';
import Config from 'react-native-config';
import { colorPalette } from '@shared/config/colorsConfig';
import { typography } from '@shared/config/typographyConfig';
import { applyChatBubbleColors } from '@shared/config/chatTheme';
import { MMKV } from 'react-native-mmkv';
import {
  SafeAreaProvider,
} from 'react-native-safe-area-context';
import Toast, { BaseToast, ErrorToast } from 'react-native-toast-message';
import { GlobalStatusLayout } from '@shared/components/GlobalStatusHeader';
import { NetworkStatusListener } from '@shared/components/NetworkStatusListener';
import "./app";
import "./app/_modules/chat";
import "./app/_modules/core";
import "./app/_modules/documents";
import "./app/_modules/history";
import "./app/_modules/user";
import { OnboardingProvider, OnboardingTourHost } from './app/_modules/onboarding';
import RootNavigator from './app/navigation/RootNavigator';
import "./app/services/SendbirdServices";
import { useClaritySession } from '@shared/hooks/useClaritySession';
import { useRemoteConfigSession } from '@shared/hooks/useRemoteConfigSession';
import "./global.css";


import { Telemetry } from 'app/services/telemetry/Telemetry';
import { postHog } from 'app/services/PostHogManager';
import { generateLocalizationConfig } from '@shared/config';

Telemetry.init();
postHog.init();

const mmkv = new MMKV();

function App() {
  const queryClient = useRef(new QueryClient()).current;
  const sbAppID = Config.SB_APP_ID;
  const { t } = useTranslation();
  const clarityEnabled = Config.ENVIRONMENT === 'production' && Config.CLARITY_ENABLED === 'true';
  useClaritySession({clarityEnabled});
  useRemoteConfigSession();

  React.useEffect(() => {
    Telemetry.breadcrumb('app.mounted');


    notifee.getInitialNotification().then((initialNotification) => {
      if (initialNotification?.notification?.data) {
        setTimeout(() => {
          handleNotificationNavigation(initialNotification.notification.data);
        }, 1000);
      }
    });

    const unsubscribeForeground = notifee.onForegroundEvent(({ type, detail }) => {
      if (type === EventType.PRESS && detail.notification?.data) {
        setTimeout(() => {
          handleNotificationNavigation(detail.notification!.data);
        }, 1500);
      }
    });

    notifee.onBackgroundEvent(async ({ type, detail }) => {
      if (type === EventType.PRESS && detail.notification?.data) {
        handleNotificationNavigation(detail.notification.data);
      }
    });

    return () => {
      unsubscribeForeground();
    };
  }, []);

  LightUIKitTheme.palette = {
    ...Palette,
    primary300: colorPalette.secondary[600],
    background300: colorPalette.primary[700]

  };
  applyChatBubbleColors(LightUIKitTheme);
  LightUIKitTheme.typography = createTypography({
    h1: {
      fontSize: typography.fontSize.xl,
      lineHeight: typography.lineHeight.xl,
    },
    h2: {
      fontSize: typography.fontSize.base,
      lineHeight: typography.lineHeight.base,
    },
    shared: {
      fontFamily: 'Lato-Bold',
    },
  });

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <KeyboardProvider>
      <QueryClientProvider client={queryClient}>
        <SendbirdUIKitContainer
          localization={{ stringSet: generateLocalizationConfig(t as (key: string) => string) }}
          chatOptions={{ localCacheStorage: mmkv }}
          appId={sbAppID!}
          platformServices={PlatformServices}
        >
          <I18nProvider>
            <SafeAreaProvider>
              <NetworkStatusListener>
                <ErrorBoundary>
                  <AuthProvider>
                    <UserProvider>
                      <PushNotificationInitializer>
                        <OnboardingProvider>
                          <OnboardingTourHost>
                            <GlobalStatusLayout>
                              <RootNavigator />
                            </GlobalStatusLayout>
                          </OnboardingTourHost>
                        </OnboardingProvider>
                      </PushNotificationInitializer>
                    </UserProvider>
                  </AuthProvider>
                </ErrorBoundary>
              </NetworkStatusListener>
              <StatusBar hidden />
            </SafeAreaProvider>
          </I18nProvider>
        </SendbirdUIKitContainer>
        <Toast config={{
          success: props => (
            <BaseToast
              {...props}
              text2NumberOfLines={3}
              style={{ borderLeftColor: colorPalette.success[500], backgroundColor: colorPalette.success[100] }}
              text1Style={{ color: colorPalette.success[700] }}
              text2Style={{ color: colorPalette.success[700] }}
            />
          ),
          error: props => <ErrorToast {...props} text2NumberOfLines={3} />,
        }} />
      </QueryClientProvider>
      </KeyboardProvider>
    </GestureHandlerRootView>
  );
}


export default App;
